import { capitalize } from 'vue'
import { arrayToEntityDict, type Entity, type EntityDict } from '../model'

const initTimeoutMs = 3000

type Database = { current: IDBDatabase | null }

function openDatabase(name: string, version: number, storeNames: string[]) {
  const database: Database = { current: null }
  const init = new Promise<void>(resolve => {
    const request = indexedDB.open(name, version)
    let abandoned = false
    const timeout = setTimeout(() => {
      console.error('Cache init timeout')
      abandoned = true
      resolve()
    }, initTimeoutMs)

    request.onsuccess = () => {
      clearTimeout(timeout)
      if (abandoned) {
        request.result.close()
        return
      }
      database.current = request.result
      database.current.onversionchange = () => {
        database.current?.close()
        database.current = null
      }
      resolve()
    }
    request.onerror = () => {
      clearTimeout(timeout)
      console.error('Cache init failure', request.error)
      resolve()
    }
    request.onblocked = () => {
      clearTimeout(timeout)
      console.error('Cache init blocked')
      abandoned = true
      resolve()
    }

    request.onupgradeneeded = event => {
      const upgraded = (event.target as IDBOpenDBRequest).result
      for (const storeName of Array.from(upgraded.objectStoreNames)) {
        upgraded.deleteObjectStore(storeName)
      }
      for (const storeName of storeNames) {
        const store = upgraded.createObjectStore(storeName)
        store.createIndex('type', 'type', { unique: false })
      }
    }
  })
  return { database, init }
}

export function createCaches<T extends string>(name: string, version: number, storeNames: T[]) {
  const { database, init } = openDatabase(name, version, storeNames)
  return Object.fromEntries(storeNames.map(x => [x, new Cache(x, database, init)])) as Record<T, Cache>
}

export class Cache {
  constructor(
    private name: string,
    private database: Database,
    private init: Promise<void>
  ) {}

  private getTransaction(mode: 'readonly' | 'readwrite'): IDBTransaction | null {
    if (!this.database.current) {
      return null
    }
    try {
      return this.database.current.transaction([this.name], mode)
    } catch (error) {
      console.error('Cache transaction failure', error)
      return null
    }
  }

  private getStore(mode: 'readonly' | 'readwrite'): IDBObjectStore | null {
    const transaction = this.getTransaction(mode)
    return transaction ? transaction.objectStore(this.name) : null
  }

  async get<T>(id: string): Promise<T | undefined> {
    await this.init
    const store = this.getStore('readonly')
    if (!store) {
      return undefined
    }
    return new Promise(resolve => {
      const request = store.get(id) as IDBRequest<T>
      request.onsuccess = () => resolve(request.result || undefined)
      request.onerror = () => {
        console.error(`Cache get failure ${id}`, request.error)
        resolve(undefined)
      }
    })
  }

  async set<T>(id: string, value: T): Promise<void> {
    await this.init
    const store = this.getStore('readwrite')
    if (!store) {
      return
    }

    return new Promise(resolve => {
      const request = store.put(value, id)
      request.onsuccess = () => resolve()
      request.onerror = () => {
        console.error(`Cache set failure ${id}`, request.error)
        resolve()
      }
    })
  }

  async setItems(items: { id: string }[]): Promise<void> {
    if (items.length === 0) {
      return
    }

    await this.init
    const transaction = this.getTransaction('readwrite')
    if (!transaction) {
      return
    }
    return new Promise(resolve => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => {
        console.error(`Cache setMany failure`, transaction.error)
        resolve()
      }
      const store = transaction.objectStore(this.name)
      items.forEach(item => store.put(item, item.id))
      transaction.commit()
    })
  }

  async deleteItems(ids: string[]): Promise<void> {
    if (ids.length === 0) {
      return
    }

    await this.init
    const transaction = this.getTransaction('readwrite')
    if (!transaction) {
      return
    }
    return new Promise(resolve => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => {
        console.error(`Cache deleteItems failure`, transaction.error)
        resolve()
      }
      const store = transaction.objectStore(this.name)
      ids.forEach(id => store.delete(id))
      transaction.commit()
    })
  }

  async deleteByType(type: string): Promise<void> {
    await this.init
    const transaction = this.getTransaction('readwrite')
    if (!transaction) {
      return
    }

    return new Promise(resolve => {
      const store = transaction.objectStore(this.name)
      const index = store.index('type')
      const range = IDBKeyRange.only(capitalize(type))
      const cursorRequest = index.openCursor(range)
      transaction.oncomplete = () => resolve()
      cursorRequest.onerror = transaction.onerror = () => {
        console.error(`Cache deleteByType failure`, transaction.error)
        resolve()
      }
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        }
      }
    })
  }

  async getByType<T extends Entity>(type: string): Promise<EntityDict<T>> {
    await this.init
    const store = this.getStore('readonly')
    if (!store) {
      return {}
    }

    return new Promise(resolve => {
      const index = store.index('type')
      const request = index.getAll(capitalize(type))
      request.onsuccess = () => resolve(arrayToEntityDict(request.result as T[]))
      request.onerror = () => {
        console.error(`Cache getByType failure ${type}`, request.error)
        resolve({})
      }
    })
  }

  async clear(): Promise<void> {
    await this.init
    const store = this.getStore('readwrite')
    if (!store) {
      return
    }
    return new Promise(resolve => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => {
        console.error(`Cache clear failure`, request.error)
        resolve()
      }
    })
  }
}
